import React, { useEffect, useState } from 'react';

import {
  Alert,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import { MSG_MAX_LENGTH, MSG_REQUIRED } from 'constant/ValidationMessages';
import { deleteAssessment } from 'stores/AssessmentStoreAdapter';
import { MasterTypeResult, getDisposalReason } from 'stores/MasterTypeStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

type AssessmentDisposalDialogProps = {
  open: boolean;
  assessmentId: string;
  onClose: () => void;
  onDisposed: () => void;
  setProgress: (v: boolean) => void;
};

const AssessmentDisposalDialog: React.FC<AssessmentDisposalDialogProps> = ({
  open,
  assessmentId,
  onClose,
  onDisposed,
  setProgress,
}) => {
  const [disposalReasonCode, setDisposalReasonCode] = useState('');
  const [disposalDetail, setDisposalDetail] = useState('');
  const [disposedAt, setDisposedAt] = useState('');

  const [disposalReasons, setDisposalReasons] = useState<MasterTypeResult[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      getDisposalReason()
        .then(setDisposalReasons)
        .catch(() => errorDefault('除却理由の取得に失敗しました'));
      setDisposalReasonCode('');
      setDisposalDetail('');
      setDisposedAt('');
      setErrors({});
    }
  }, [open]);

  const isFormValid = !!disposalReasonCode && !!disposedAt;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!disposalReasonCode) newErrors.disposalReasonCode = MSG_REQUIRED('除却理由');
    if (!disposedAt) newErrors.disposedAt = MSG_REQUIRED('除却日');
    if (disposalDetail && disposalDetail.length > 300)
      newErrors.disposalDetail = MSG_MAX_LENGTH('除却詳細', 300);

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleExecute = () => {
    if (!validate()) return;

    setProgress(true);
    deleteAssessment({
      assessmentId,
      disposalReasonCode,
      ...(disposalDetail && { disposalDetail }),
      disposedAt,
    })
      .then(() => {
        successDefault('資産を除却しました');
        onDisposed();
      })
      .catch((e: Error) => {
        errorDefault('資産の除却に失敗しました', e.message);
      })
      .finally(() => setProgress(false));
  };

  return (
    <DeleteConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={handleExecute}
      title="資産除却"
      alertConfirmText="delete"
      confirmDisabled={!isFormValid}
    >
      <Alert severity="warning" sx={{ mb: 2 }}>
        一度除却した資産は戻せません。実行しますか？
      </Alert>

      <Grid container spacing={2}>
        <Grid size={12}>
          <FormControl fullWidth size="small" required error={!!errors.disposalReasonCode}>
            <InputLabel>除却理由</InputLabel>
            <Select
              value={disposalReasonCode}
              onChange={(e) => setDisposalReasonCode(e.target.value)}
              label="除却理由"
              sx={{ borderRadius: 2 }}
            >
              {disposalReasons.map((r) => (
                <MenuItem key={r.typeCode} value={r.typeCode}>
                  {r.typeLabel}
                </MenuItem>
              ))}
            </Select>
            {errors.disposalReasonCode && (
              <FormHelperText>{errors.disposalReasonCode}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid size={12}>
          <TextField
            label="除却詳細"
            value={disposalDetail}
            onChange={(e) => setDisposalDetail(e.target.value)}
            fullWidth
            size="small"
            error={!!errors.disposalDetail}
            helperText={errors.disposalDetail}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Grid>
        <Grid size={12}>
          <FinancialYearMonthSelect
            dateType="end"
            value={disposedAt}
            onChange={setDisposedAt}
            label="除却日"
            required
            error={!!errors.disposedAt}
            helperText={errors.disposedAt}
          />
        </Grid>
      </Grid>
    </DeleteConfirmDialog>
  );
};

export default AssessmentDisposalDialog;
